import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '@/shared/auth';
import { Grid, GridPageLayout, useGridConfig, type GridColumn, type GridPageTab } from '@/shared/components/Grid';
import { formatearExcelConSeparador } from '@/shared/formula/excelFormat';
import { LibraryGridConfig } from './LibraryGridConfig';
import { recordLibraryOpen } from './libraryRecents';
import { useI18n } from '@/shared/i18n/useT';
import {
  HOLIDAY_COUNTRIES,
  loadLibraryExchangeRates,
  loadLibraryHolidays,
  type HolidayCountry,
  type LibraryExchangeRates,
  type LibraryHolidays,
  type RateQuote,
} from './libraryApi';

/**
 * One library document, laid out like any sheet (GridPageLayout + Grid) but
 * read-only for everyone: the data is public reference data assembled by the
 * Worker, so every viewer sees the same cells (AGENTS.md invariant 12).
 *
 * `/library/<document>/<sheet>`; the sheet is a country or a currency.
 */
export function LibraryDocumentPage() {
  const { documentId, sheet } = useParams();
  // Last opened on Home; once per document visit, not per sheet switch.
  useEffect(() => {
    if (documentId === 'holidays' || documentId === 'exchange-rates') recordLibraryOpen(documentId);
  }, [documentId]);
  if (documentId === 'holidays') return <HolidaysDocument sheet={sheet} />;
  if (documentId === 'exchange-rates') return <ExchangeRatesDocument sheet={sheet} />;
  return <Navigate replace to="/library" />;
}

function useAccessToken() {
  return useSession().session?.access_token ?? null;
}

/** A `YYYY-MM-DD` day in one of the organization's date patterns (Settings →
 *  Formats), so library dates read like every other grid's. Local midnight,
 *  because the pattern formatter reads local date parts. */
function formatIsoDay(iso: string, pattern: string, separator: '.' | ',') {
  const [y, m, d] = iso.split('-').map(Number);
  return formatearExcelConSeparador(new Date(y!, m! - 1, d!), pattern, separator).texto;
}

interface HolidayRow {
  readonly id: string;
  readonly date: string;
  readonly year: string;
  readonly name: string;
  readonly type: string;
  readonly notes: string;
}

function HolidaysDocument({ sheet }: { sheet: string | undefined }) {
  const { t, formatLocale } = useI18n();
  const { formats, decimalSeparator } = useGridConfig();
  const navigate = useNavigate();
  const token = useAccessToken();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState<LibraryHolidays | null>(null);
  const [error, setError] = useState<string | null>(null);

  const country = (HOLIDAY_COUNTRIES as readonly string[]).includes(sheet?.toUpperCase() ?? '')
    ? (sheet!.toUpperCase() as HolidayCountry)
    : null;

  useEffect(() => {
    document.title = t('library.holidays.title');
  }, [t]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setData(null);
    setError(null);
    loadLibraryHolidays(year, token, controller.signal)
      .then(setData)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          console.error('[library] holidays', cause);
          setError(t('library.error'));
        }
      });
    return () => controller.abort();
  }, [t, token, year]);

  // Sheets in alphabetical order of the country's name in the reader's language.
  const tabs = useMemo<GridPageTab[]>(
    () =>
      [...HOLIDAY_COUNTRIES]
        .sort((a, b) => t(`library.countries.${a}`).localeCompare(t(`library.countries.${b}`), formatLocale))
        .map((code) => ({ href: `/library/holidays/${code.toLowerCase()}`, labelKey: t(`library.countries.${code}`) })),
    [formatLocale, t],
  );

  const result = country ? data?.countries[country] : undefined;
  const rows = useMemo<HolidayRow[]>(
    () =>
      result && 'days' in result
        ? result.days.map((day, index) => ({ id: `${day.date}:${index}`, year: day.date.slice(0, 4), ...day }))
        : [],
    [result],
  );

  const columns = useMemo<GridColumn<HolidayRow>[]>(
    () => [
      {
        field: 'date',
        headerName: t('library.holidays.columns.date'),
        cellType: 'date',
        minWidth: 140,
        valueFormatter: ({ value }) => (typeof value === 'string' ? formatIsoDay(value, formats.dateWeekday, decimalSeparator) : ''),
      },
      { field: 'year', headerName: t('library.holidays.columns.year'), minWidth: 70 },
      { field: 'name', headerName: t('library.holidays.columns.name'), minWidth: 220, flexible: true },
      { field: 'type', headerName: t('library.holidays.columns.type'), minWidth: 180 },
      { field: 'notes', headerName: t('library.holidays.columns.notes'), minWidth: 220, multiline: true },
    ],
    [decimalSeparator, formats.dateWeekday, t],
  );

  if (!country) return <Navigate replace to={tabs[0]!.href} />;

  return (
    <GridPageLayout
      fullBleed
      title={t('library.holidays.title')}
      tabs={tabs}
      activeTab={`/library/holidays/${country.toLowerCase()}`}
      onTabChange={(tab) => navigate(tab.href)}
      error={error ?? (result && 'error' in result ? t('library.error') : null)}
      actions={
        <label className="flex items-center gap-2 text-sm text-muted">
          {t('library.holidays.year')}
          <select
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
            className="tap-target rounded-token border border-border bg-surface px-2 text-fg"
          >
            {Array.from({ length: 5 }, (_, i) => thisYear - 2 + i).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      }
    >
      <p className="mb-2 text-sm text-muted">{t('library.readOnly')}</p>
      <LibraryGridConfig defaultChart={holidaysChart}>
        <Grid
          gridKey={`library-holidays-${country}`}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={!data && !error}
          editable={false}
        />
      </LibraryGridConfig>
    </GridPageLayout>
  );
}

/** Holidays carry no numbers to plot, but the chart still opens on dates. */
const holidaysChart = () => ({ type: 'line' as const, category: 'date', showLabels: false });

type RateRow = { readonly id: string; readonly date: string } & Record<string, string | number | null>;

const QUOTE_SIDES = ['buy', 'sell', 'avg'] as const satisfies readonly (keyof RateQuote)[];

function ExchangeRatesDocument({ sheet }: { sheet: string | undefined }) {
  const { t, formatLocale } = useI18n();
  const { formats, decimalSeparator } = useGridConfig();
  const navigate = useNavigate();
  const token = useAccessToken();
  const [data, setData] = useState<LibraryExchangeRates | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = t('library.exchangeRates.title');
  }, [t]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    loadLibraryExchangeRates(token, controller.signal)
      .then(setData)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          console.error('[library] exchange rates', cause);
          setError(t('library.error'));
        }
      });
    return () => controller.abort();
  }, [t, token]);

  const tabs = useMemo<GridPageTab[]>(
    () =>
      (data?.sheets ?? []).map((s) => ({
        href: `/library/exchange-rates/${s.currency.toLowerCase()}`,
        // GridPageLayout prints labelKey as-is (sheet names are user text).
        labelKey: s.currency,
      })),
    [data],
  );

  const active = data?.sheets.find((s) => s.currency.toLowerCase() === sheet?.toLowerCase()) ?? null;

  const numberFormat = useMemo(
    () => new Intl.NumberFormat(formatLocale, { maximumSignificantDigits: 6, maximumFractionDigits: 4 }),
    [formatLocale],
  );

  const columns = useMemo<GridColumn<RateRow>[]>(() => {
    if (!active) return [];
    return [
      {
        field: 'date',
        headerName: t('library.exchangeRates.columns.date'),
        cellType: 'date',
        minWidth: 110,
        valueFormatter: ({ value }) => (typeof value === 'string' ? formatIsoDay(value, formats.dateWeekday, decimalSeparator) : ''),
      },
      ...active.columns.flatMap((column) => {
        // An official mid rate has no spread: one column, not three equal ones.
        const hasSpread = active.rows.some(({ values }) => {
          const quote = values[column];
          return quote !== undefined && quote.buy !== quote.sell;
        });
        if (!hasSpread) {
          return [{
            field: `${column}|avg`,
            headerName: column,
            cellType: 'number',
            minWidth: 90,
            valueFormatter: ({ value }) => (typeof value === 'number' ? numberFormat.format(value) : ''),
          } satisfies GridColumn<RateRow>];
        }
        return QUOTE_SIDES.map<GridColumn<RateRow>>((side) => ({
          field: `${column}|${side}`,
          headerName: t(`library.exchangeRates.columns.${side}`),
          headerGroup: column,
          cellType: 'number',
          minWidth: 90,
          valueFormatter: ({ value }) => (typeof value === 'number' ? numberFormat.format(value) : ''),
        }));
      }),
    ];
  }, [active, decimalSeparator, formats.dateWeekday, numberFormat, t]);

  const rows = useMemo<RateRow[]>(
    () =>
      // A day no source has published yet (today, before the close) is noise.
      (active?.rows ?? []).filter((row) => Object.values(row.values).some((q) => q.avg !== null || q.buy !== null || q.sell !== null)).map((row) => {
        const out: Record<string, string | number | null> = { id: row.date, date: row.date };
        for (const [column, quote] of Object.entries(row.values)) {
          for (const side of QUOTE_SIDES) out[`${column}|${side}`] = quote[side];
        }
        return out as RateRow;
      }),
    [active],
  );

  // Until the reader picks one: a line over time of the sheet's first rate.
  const firstSeries = active?.columns[0] ? `${active.columns[0]}|avg` : undefined;
  const fxChart = useCallback(
    () => ({ type: 'line' as const, category: 'date', series: firstSeries ? [firstSeries] : [], showLabels: false }),
    [firstSeries],
  );

  if (data && !active && tabs[0]) return <Navigate replace to={tabs[0].href} />;

  return (
    <GridPageLayout
      fullBleed
      title={t('library.exchangeRates.title')}
      tabs={tabs}
      activeTab={active ? `/library/exchange-rates/${active.currency.toLowerCase()}` : undefined}
      onTabChange={(tab) => navigate(tab.href)}
      error={error}
    >
      <p className="mb-2 text-sm text-muted">
        {t('library.readOnly')}
        {active && ` ${t(['USD', 'EUR'].includes(active.currency) ? 'library.exchangeRates.perBase' : 'library.exchangeRates.perQuote', { currency: active.currency })}`}
        {active?.notes && ` ${active.notes}`}
        {data && ` ${t('library.updatedAt', { date: new Date(data.updatedAt).toLocaleString(formatLocale) })}`}
      </p>
      <LibraryGridConfig defaultChart={fxChart}>
        <Grid
          gridKey={`library-fx-${active?.currency ?? 'none'}`}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={!data && !error}
          editable={false}
        />
      </LibraryGridConfig>
    </GridPageLayout>
  );
}
